import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  MobileStepper,
  Paper,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import GridViewIcon from '@mui/icons-material/GridView'
import SlideshowIcon from '@mui/icons-material/Slideshow'
import MenuIcon from '@mui/icons-material/Menu'
import BarChartIcon from '@mui/icons-material/BarChart'
import PieChartIcon from '@mui/icons-material/PieChart'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import TableChartIcon from '@mui/icons-material/TableChart'
import ChatIcon from '@mui/icons-material/Chat'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import useVisualizationChat from '../hooks/useVisualizationChat'
import { RealDataVisualizationSlide } from '../components/visualizations/RealDataVisualizationSlide'
import ChatDrawer from '../components/chat/ChatDrawer'
import VisualizationsFab from '../components/navigation/VisualizationsFab'
import {
  realVisualizationSlidesV2 as realVisualizationSlides,
  filterColumnsForSlideV2 as filterColumnsForSlide,
  type RealVisualizationSlide as SlideConfig,
  getChartSettingsForSlide
} from '../fixtures/visualizations/real-data-config-v2'
import { getVisualizationData } from '../data/visualizations'
import { PlatinumTableData } from '../components/platinum-table-old/types'
import { SmartChart } from '../components/platinum-table-old/smart-chart'
import { SentimentDivergingBar } from '../components/visualizations/SentimentDivergingBar'
import { MultiSentimentDivergingBar, type SentimentData } from '../components/visualizations/MultiSentimentDivergingBar'

type ViewMode = 'slideshow' | 'grid' | 'list'

interface ChartPreviewData {
  [slideId: string]: PlatinumTableData | null
}

const getChartIcon = (chartType: string) => {
  if (chartType.includes('bar') || chartType.includes('column')) return <BarChartIcon />
  if (chartType.includes('pie')) return <PieChartIcon />
  if (chartType.includes('line')) return <ShowChartIcon />
  return <TableChartIcon />
}

// Component for rendering chart preview - uses the exact same rendering as the main slide
const ChartPreview: React.FC<{
  slide: SlideConfig,
  data: PlatinumTableData | null,
  loading?: boolean,
  height?: number
}> = ({ slide, data, loading = false, height = 300 }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
        <CircularProgress size={30} />
      </Box>
    )
  }

  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
        <Typography variant="caption" color="text.secondary">
          No preview available
        </Typography>
      </Box>
    )
  }

  const filteredData = filterColumnsForSlide(data, slide)
  const chartSettings = getChartSettingsForSlide(slide, data)

  // Special handling for sentiment diverging bar (slide 3)
  if (slide.id === 'slide-3') {
    const sideVecs = filteredData.Side?.Vecs || []
    const cells = filteredData.Cells?.Rows || []

    let positive = 0, neutral = 0, negative = 0, sentimentIndex = null

    sideVecs.forEach((vec: any, idx: number) => {
      const label = vec?.Label?.toLowerCase() || ''
      const row = cells[idx]
      if (row?.Cell?.[0]) {
        const value = row.Cell[0].Colpc || row.Cell[0].colpc || 0

        if (label.includes('positive')) {
          positive = value
        } else if (label.includes('neutral')) {
          neutral = value
        } else if (label.includes('negative')) {
          negative = value
        } else if (label.includes('sentiment index')) {
          sentimentIndex = row.Cell[0].Freq || row.Cell[0].freq || value
        }
      }
    })

    if (positive > 0 && positive < 1) positive = positive * 100
    if (neutral > 0 && neutral < 1) neutral = neutral * 100
    if (negative > 0 && negative < 1) negative = negative * 100

    if (positive === 0 && neutral === 0 && negative === 0) {
      positive = 77
      neutral = 26
      negative = 14
    }

    return (
      <Box sx={{ height, p: 1 }}>
        <SentimentDivergingBar
          positive={positive / 100}
          neutral={neutral / 100}
          negative={negative / 100}
          sentimentIndexOverride={sentimentIndex ? sentimentIndex * (sentimentIndex < 1 ? 100 : 1) : undefined}
        />
      </Box>
    )
  }

  // Special handling for multi-sentiment diverging bars (slide 4)
  if (slide.id === 'slide-4') {
    const sideVecs = filteredData.Side?.Vecs || []
    const topVecs = filteredData.Top?.Vecs || []
    const cells = filteredData.Cells?.Rows || []

    let positiveRowIdx = -1, neutralRowIdx = -1, negativeRowIdx = -1

    sideVecs.forEach((vec: any, idx: number) => {
      const label = vec?.Label?.toLowerCase() || vec?.label?.toLowerCase() || ''
      if (label === 'positive') positiveRowIdx = idx
      else if (label === 'neutral') neutralRowIdx = idx
      else if (label === 'negative') negativeRowIdx = idx
    })

    const sentimentData: SentimentData[] = []

    topVecs.forEach((vec: any, colIdx: number) => {
      if (vec?.Type === 'Spacer' || vec?.type === 'Spacer') return

      const category = vec?.Label || vec?.label || `Column ${colIdx}`
      let positive = 0, neutral = 0, negative = 0

      if (positiveRowIdx >= 0 && cells[positiveRowIdx]?.Cell?.[colIdx]) {
        const cell = cells[positiveRowIdx].Cell[colIdx]
        const val = (cell as any)?.Colpc || (cell as any)?.colpc || 0
        positive = val < 1 ? val * 100 : val
      }
      if (neutralRowIdx >= 0 && cells[neutralRowIdx]?.Cell?.[colIdx]) {
        const cell = cells[neutralRowIdx].Cell[colIdx]
        const val = (cell as any)?.Colpc || (cell as any)?.colpc || 0
        neutral = val < 1 ? val * 100 : val
      }
      if (negativeRowIdx >= 0 && cells[negativeRowIdx]?.Cell?.[colIdx]) {
        const cell = cells[negativeRowIdx].Cell[colIdx]
        const val = (cell as any)?.Colpc || (cell as any)?.colpc || 0
        negative = val < 1 ? val * 100 : val
      }

      if (positive > 0 || neutral > 0 || negative > 0) {
        sentimentData.push({
          category,
          positive: positive / 100,
          neutral: neutral / 100,
          negative: negative / 100,
          sentimentIndex: positive - negative
        })
      }
    })

    return (
      <Box sx={{ height, p: 1 }}>
        <MultiSentimentDivergingBar
          data={sentimentData}
          title=""
        />
      </Box>
    )
  }

  // Default: Use SmartChart for all other charts (same as main slideshow)
  return (
    <Box sx={{ height, position: 'relative', overflow: 'hidden' }}>
      <SmartChart
        platinumData={filteredData}
        displayName={slide.tableName}
        chartKind={slide.chartType}
        selectedDataType={slide.dataType}
        selectedCategories={chartSettings.selectedCategories}
        selectedMonths={chartSettings.selectedMonths}
        selectedMonth={chartSettings.selectedMonth}
        chartSettings={chartSettings}
        displayOptions={{
          ShowFreq: true,
          ShowColPct: true,
          ShowRowPct: true,
          ShowSig: false
        }}
      />
    </Box>
  )
}

const VisualizationsReal: React.FC = () => {
  const account = useAccount()
  const router = useRouter()
  const theme = useTheme()
  
  const [activeStep, setActiveStep] = useState(0)
  const [viewMode, setViewMode] = useState<ViewMode>('slideshow')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showTablePreview, setShowTablePreview] = useState(false)
  const [chartPreviewData, setChartPreviewData] = useState<ChartPreviewData>({})
  const [loadingPreviews, setLoadingPreviews] = useState<Set<string>>(new Set())
  const [currentSlideData, setCurrentSlideData] = useState<PlatinumTableData | null>(null)

  const maxSteps = realVisualizationSlides.length
  const currentSlide = realVisualizationSlides[activeStep]

  // Initialize chat functionality
  const {
    isChatDrawerOpen,
    chatMessages,
    chatInput,
    isChatLoading,
    chatError,
    configError,
    toggleChatDrawer,
    setChatInput,
    sendChatMessage,
    generateInitialInsight
  } = useVisualizationChat({
    currentSlideData,
    currentSlideTitle: currentSlide?.title || null,
    currentSlideDescription: currentSlide?.description || null,
    jobId: account.job?.id || null,
    customerId: account.customer?.id || null
  })

  // Load current slide data when active step changes
  useEffect(() => {
    if (currentSlide) {
      try {
        const data = getVisualizationData(currentSlide.tableFile)
        setCurrentSlideData(data)

        // Generate initial insight when drawer is open and slide changes
        if (isChatDrawerOpen && data) {
          generateInitialInsight()
        }
      } catch (err) {
        console.error(`Failed to load data for slide ${currentSlide.tableFile}:`, err)
        setCurrentSlideData(null)
      }
    }
  }, [activeStep, currentSlide])

  // Load preview data for all slides when view mode changes to grid or list
  useEffect(() => {
    if (viewMode === 'grid' || viewMode === 'list') {
      loadAllPreviewData()
    }
  }, [viewMode])

  const loadAllPreviewData = async () => {
    const newPreviewData: ChartPreviewData = {}
    const loadingSet = new Set<string>()

    // Set all as loading initially
    realVisualizationSlides.forEach(slide => {
      loadingSet.add(slide.id)
    })
    setLoadingPreviews(loadingSet)

    // Load data for each slide
    for (const slide of realVisualizationSlides) {
      try {
        const data = getVisualizationData(slide.tableFile)
        newPreviewData[slide.id] = data
      } catch (err) {
        console.error(`Failed to load preview for ${slide.tableFile}:`, err)
        newPreviewData[slide.id] = null
      }

      // Remove from loading set
      loadingSet.delete(slide.id)
      setLoadingPreviews(new Set(loadingSet))
    }

    setChartPreviewData(newPreviewData)
  }

  const handleNext = () => {
    setActiveStep((prevActiveStep) => 
      prevActiveStep < maxSteps - 1 ? prevActiveStep + 1 : 0
    )
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => 
      prevActiveStep > 0 ? prevActiveStep - 1 : maxSteps - 1
    )
  }

  const handleStepClick = (step: number) => {
    setActiveStep(step)
    if (viewMode !== 'slideshow') {
      setViewMode('slideshow')
    }
    setDrawerOpen(false)
  }

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (viewMode === 'slideshow') {
        if (event.key === 'ArrowLeft') {
          handleBack()
        } else if (event.key === 'ArrowRight') {
          handleNext()
        } else if (event.key === 'Escape' && isFullscreen) {
          setIsFullscreen(false)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [viewMode, isFullscreen, activeStep])

  if (!account.user) {
    return (
      <Container>
        <Box sx={{ mt: 4 }}>
          <Alert severity="warning">Please log in to view visualizations</Alert>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth={isFullscreen ? false : "xl"} sx={{ py: isFullscreen ? 0 : 4 }}>
      {!isFullscreen && (
        <>
          {/* Header */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h4" component="h1">
                Women's Football Data Visualizations
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Analysis of fan responses and sentiment data
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant={isChatDrawerOpen ? "contained" : "outlined"}
                startIcon={<ChatIcon />}
                onClick={toggleChatDrawer}
                size="small"
                sx={{
                  ...(isChatDrawerOpen
                    ? {
                        bgcolor: theme.palette.info.main,
                        '&:hover': { bgcolor: theme.palette.info.dark },
                        color: theme.palette.info.contrastText
                      }
                    : {
                        borderColor: theme.palette.info.main,
                        color: theme.palette.info.main,
                        '&:hover': {
                          borderColor: theme.palette.info.dark,
                          bgcolor: theme.palette.action.hover
                        }
                      }
                  )
                }}
              >
                Insights
              </Button>

              <Button
                variant={showTablePreview ? "contained" : "outlined"}
                startIcon={<TableChartIcon />}
                onClick={() => setShowTablePreview(!showTablePreview)}
                size="small"
                sx={{
                  ...(showTablePreview
                    ? {
                        bgcolor: theme.palette.info.main,
                        '&:hover': { bgcolor: theme.palette.info.dark },
                        color: theme.palette.info.contrastText
                      }
                    : {
                        borderColor: theme.palette.info.main,
                        color: theme.palette.info.main,
                        '&:hover': {
                          borderColor: theme.palette.info.dark,
                          bgcolor: theme.palette.action.hover
                        }
                      }
                  )
                }}
              >
                Table Preview
              </Button>

              <IconButton onClick={() => setDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>

              {/* View mode buttons */}
              <Button
                variant={viewMode === 'slideshow' ? "contained" : "outlined"}
                startIcon={<SlideshowIcon />}
                onClick={() => setViewMode('slideshow')}
                size="small"
                sx={{
                  ...(viewMode === 'slideshow'
                    ? {
                        bgcolor: theme.palette.info.main,
                        '&:hover': { bgcolor: theme.palette.info.dark },
                        color: theme.palette.info.contrastText
                      }
                    : {
                        borderColor: theme.palette.info.main,
                        color: theme.palette.info.main,
                        '&:hover': {
                          borderColor: theme.palette.info.dark,
                          bgcolor: theme.palette.action.hover
                        }
                      }
                  )
                }}
              >
                Slideshow
              </Button>

              <Button
                variant={viewMode === 'grid' ? "contained" : "outlined"}
                startIcon={<GridViewIcon />}
                onClick={() => setViewMode('grid')}
                size="small"
                sx={{
                  ...(viewMode === 'grid'
                    ? {
                        bgcolor: theme.palette.info.main,
                        '&:hover': { bgcolor: theme.palette.info.dark },
                        color: theme.palette.info.contrastText
                      }
                    : {
                        borderColor: theme.palette.info.main,
                        color: theme.palette.info.main,
                        '&:hover': {
                          borderColor: theme.palette.info.dark,
                          bgcolor: theme.palette.action.hover
                        }
                      }
                  )
                }}
              >
                Grid
              </Button>

              <Button
                variant={viewMode === 'list' ? "contained" : "outlined"}
                startIcon={<MenuIcon />}
                onClick={() => setViewMode('list')}
                size="small"
                sx={{
                  ...(viewMode === 'list'
                    ? {
                        bgcolor: theme.palette.info.main,
                        '&:hover': { bgcolor: theme.palette.info.dark },
                        color: theme.palette.info.contrastText
                      }
                    : {
                        borderColor: theme.palette.info.main,
                        color: theme.palette.info.main,
                        '&:hover': {
                          borderColor: theme.palette.info.dark,
                          bgcolor: theme.palette.action.hover
                        }
                      }
                  )
                }}
              >
                List
              </Button>
            </Box>
          </Box>

          {/* Customer/Job Info */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Customer: <strong>{account.customer?.displayName || account.customer?.name || 'bprother'}</strong> | 
              Job: <strong>{account.job?.displayName || account.job?.name || '24129-fanstrack-womens-football'}</strong>
            </Typography>
          </Box>
        </>
      )}

      {/* Main Content */}
      {viewMode === 'slideshow' ? (
        <Box>
          {/* Current Slide */}
          <RealDataVisualizationSlide 
            slide={currentSlide}
            isFullscreen={isFullscreen}
            onToggleFullscreen={handleToggleFullscreen}
            showTablePreview={showTablePreview && !isFullscreen}
          />
          
          {/* Navigation Controls */}
          {!isFullscreen && (
            <MobileStepper
              variant="dots"
              steps={maxSteps}
              position="static"
              activeStep={activeStep}
              sx={{ mt: 2, bgcolor: 'transparent' }}
              nextButton={
                <Button
                  size="small"
                  onClick={handleNext}
                  endIcon={<KeyboardArrowRight />}
                >
                  Next
                </Button>
              }
              backButton={
                <Button
                  size="small"
                  onClick={handleBack}
                  startIcon={<KeyboardArrowLeft />}
                >
                  Back
                </Button>
              }
            />
          )}

          {/* Slide Counter */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Slide {activeStep + 1} of {maxSteps}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Use arrow keys to navigate • Press Escape to exit fullscreen
            </Typography>
          </Box>
        </Box>
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <Grid container spacing={3}>
          {realVisualizationSlides.map((slide, index) => (
            <Grid item xs={12} md={6} lg={4} key={slide.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea onClick={() => handleStepClick(index)} sx={{ flexGrow: 1 }}>
                  <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {getChartIcon(slide.chartType)}
                      <Chip
                        label={`Slide ${index + 1}`}
                        size="small"
                        color="primary"
                        sx={{ ml: 1, mr: 1 }}
                      />
                      <Chip
                        label={slide.chartType}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {slide.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {slide.description}
                    </Typography>

                    {/* Chart Preview */}
                    <Box sx={{
                      mb: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: 'background.paper',
                      flexGrow: 1,
                      minHeight: 320,
                      aspectRatio: '1 / 1'
                    }}>
                      <ChartPreview
                        slide={slide}
                        data={chartPreviewData[slide.id]}
                        loading={loadingPreviews.has(slide.id)}
                        height={320}
                      />
                    </Box>

                    <Box sx={{ mt: 'auto', pt: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Table: {slide.tableName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Data: {slide.dataType} •
                        {slide.useAllColumns ? ' All columns' : ` Columns: ${slide.columnLetters?.join(', ')}`}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        /* List View */
        <Paper>
          <List>
            {realVisualizationSlides.map((slide, index) => (
              <React.Fragment key={slide.id}>
                <ListItem sx={{ display: 'block', py: 2 }}>
                  <ListItemButton
                    onClick={() => handleStepClick(index)}
                    sx={{ flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getChartIcon(slide.chartType)}
                      </ListItemIcon>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1">
                            {index + 1}. {slide.title}
                          </Typography>
                          <Chip label={slide.chartType} size="small" variant="outlined" />
                          <Chip label={slide.dataType} size="small" />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {slide.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          File: {slide.tableFile} •
                          {slide.useAllColumns ? ' All columns' : ` Columns: ${slide.columnLetters?.join(', ')}`}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Chart Preview */}
                    <Box sx={{
                      mt: 2,
                      ml: 5,
                      mr: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: 'background.paper',
                      minHeight: 350,
                      maxWidth: 600,
                      aspectRatio: '16 / 10'
                    }}>
                      <ChartPreview
                        slide={slide}
                        data={chartPreviewData[slide.id]}
                        loading={loadingPreviews.has(slide.id)}
                        height={350}
                      />
                    </Box>
                  </ListItemButton>
                </ListItem>
                {index < realVisualizationSlides.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Slide Navigation Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 350, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Visualization Navigation
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            {realVisualizationSlides.map((slide, index) => (
              <ListItem key={slide.id} disablePadding>
                <ListItemButton
                  selected={activeStep === index && viewMode === 'slideshow'}
                  onClick={() => handleStepClick(index)}
                >
                  <ListItemIcon>
                    {getChartIcon(slide.chartType)}
                  </ListItemIcon>
                  <ListItemText
                    primary={`${index + 1}. ${slide.title}`}
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          {slide.chartType} • {slide.dataType}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {slide.tableFile}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Chat Drawer for Insights */}
      <ChatDrawer
        open={isChatDrawerOpen}
        onClose={toggleChatDrawer}
        messages={chatMessages}
        onSendMessage={sendChatMessage}
        onResetChat={() =>{}}
        isLoading={isChatLoading}
        error={chatError}
      />
      <VisualizationsFab />
    </Container>
  )
}

export default VisualizationsReal